import React from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SearchInput from 'search/inputs/SearchInput';
import {Filter, FilterKey, Operator, StandardFilter} from 'search/SearchConstants';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import InfoIcon from 'ui-library/icons/InfoIcon';
import APIUtil from 'util/APIUtil';

export function getDefaultSelectedEntryOrWorksheetId(analysis: AnalysisGraphQL): string | null {
  return analysis.creationContext.worksheetId ?? analysis.creationContext.entryId ?? null;
}

export function DefaultEntryOrWorksheetInfo({analysis}: {analysis: AnalysisGraphQL}): JSX.Element | null {
  const defaultValue = getDefaultSelectedEntryOrWorksheetId(analysis);
  if (defaultValue == null) {
    return null;
  }

  assert(
    APIUtil.isEntry(defaultValue) || APIUtil.isWorksheet(defaultValue),
    'Expected defaultValue to be either an entry or worksheet ID',
    {defaultValue}
  );

  return (
    <Popover
      interaction={FloatingInteraction.HOVER}
      renderContent={() => (
        <>
          This value defaults to the {APIUtil.isEntry(defaultValue) ? 'entry' : 'worksheet'} where the
          analysis was created.
        </>
      )}
      placement={FloatingPlacement.RIGHT}
    >
      <InfoIcon />
    </Popover>
  );
}

export interface EntryAndWorksheetSearchInputProps {
  autoFocus?: boolean;
  disableTyping?: boolean;
  enableSearchEntries?: boolean;
  enableSearchWorksheets?: boolean;
  isMulti?: boolean;
  onRemove: (itemId: string) => void;
  onSelect: (itemId: string) => void;
  selected: Array<string>;
  dropLeft?: boolean;
}

export default function EntryAndWorksheetSearchInput({
  autoFocus,
  disableTyping,
  enableSearchEntries = true,
  enableSearchWorksheets = true,
  isMulti,
  onRemove,
  onSelect,
  selected,
  dropLeft,
}: EntryAndWorksheetSearchInputProps): JSX.Element {
  assert(
    enableSearchEntries || enableSearchWorksheets,
    "At least one of 'enableSearchEntries' or 'enableSearchWorksheets' must be true"
  );
  let additionalFilters: Array<Filter> = [];
  if (enableSearchEntries) {
    const isFillInEntryFilter: StandardFilter = {
      key: FilterKey.IS_FILL_IN_ENTRY,
      operator: Operator.IS_FALSE,
      value: null,
    };
    additionalFilters = [isFillInEntryFilter];
  }
  return (
    <SearchInput
      autoFocus={autoFocus}
      additionalFilters={additionalFilters}
      disableTyping={disableTyping}
      isMulti={isMulti}
      placeholder={
        enableSearchEntries && enableSearchWorksheets
          ? 'Search for an entry or worksheet'
          : enableSearchEntries
            ? 'Search for an entry'
            : 'Search for a worksheet'
      }
      onRemove={onRemove}
      onSelect={onSelect}
      selected={selected}
      dropLeft={dropLeft}
      types={[
        ...(enableSearchEntries ? [ModelTypeConstants.ENTRY] : []),
        ...(enableSearchWorksheets ? [ModelTypeConstants.WORKSHEET] : []),
      ]}
    />
  );
}
